import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];

const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

const AdminLogin = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      onLogin();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-[80vh] items-center justify-center px-6">
      <motion.form
        onSubmit={onSubmit}
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: EASE }}
        className="w-full max-w-[420px] rounded-2xl border border-[#c99a74]/25 bg-[#24140e]/60 p-10 backdrop-blur"
        data-testid="admin-login-form"
      >
        <p className="font-display text-center text-2xl tracking-[0.08em] text-[#a97855]">
          LACE <span className="font-serif-lux italic text-[#c99a74]">&amp;</span> BOND
        </p>
        <p className="mt-3 text-center text-[0.6rem] tracking-[0.35em] uppercase text-[#bda89a]">
          Private List — Admin
        </p>

        <label className="mt-9 block text-[0.6rem] tracking-[0.3em] uppercase text-[#bda89a]" htmlFor="admin-email">
          Email
        </label>
        <input
          id="admin-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoComplete="username"
          className="mt-2 w-full rounded-lg border border-[#c99a74]/30 bg-transparent px-4 py-3 text-sm tracking-[0.04em] text-[#e8d8ca] outline-none transition-colors focus:border-[#c99a74]"
          data-testid="admin-login-email"
        />

        <label className="mt-5 block text-[0.6rem] tracking-[0.3em] uppercase text-[#bda89a]" htmlFor="admin-password">
          Password
        </label>
        <input
          id="admin-password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          autoComplete="current-password"
          className="mt-2 w-full rounded-lg border border-[#c99a74]/30 bg-transparent px-4 py-3 text-sm tracking-[0.04em] text-[#e8d8ca] outline-none transition-colors focus:border-[#c99a74]"
          data-testid="admin-login-password"
        />

        {error && (
          <p className="mt-4 text-xs tracking-[0.04em] text-red-400" data-testid="admin-login-error" role="alert">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={loading}
          className="mt-9 w-full rounded-full bg-[#5b3829] py-3.5 text-[0.66rem] tracking-[0.28em] uppercase text-[#ead8ca] transition-colors duration-300 hover:bg-[#c99a74] hover:text-[#160c08] disabled:opacity-60"
          data-testid="admin-login-submit"
        >
          {loading ? "Signing in…" : "Sign In"}
        </button>
      </motion.form>
    </div>
  );
};

const ChangePassword = () => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setStatus("");
    try {
      await axios.post(
        `${API}/auth/change-password`,
        { current_password: current, new_password: next },
        { withCredentials: true }
      );
      setStatus("Password updated. Use the new one next time you sign in.");
      setCurrent("");
      setNext("");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={onSubmit}
      className="mt-8 rounded-2xl border border-[#c99a74]/20 p-6 sm:p-8"
      data-testid="admin-change-password-form"
    >
      <p className="text-[0.6rem] tracking-[0.3em] uppercase text-[#bda89a]">Change Password</p>
      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <input
          type="password"
          placeholder="Current password"
          required
          autoComplete="current-password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          className="w-full rounded-lg border border-[#c99a74]/30 bg-transparent px-4 py-3 text-sm tracking-[0.04em] text-[#e8d8ca] outline-none transition-colors focus:border-[#c99a74]"
          data-testid="admin-current-password"
        />
        <input
          type="password"
          placeholder="New password (min 8 characters)"
          required
          minLength={8}
          autoComplete="new-password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          className="w-full rounded-lg border border-[#c99a74]/30 bg-transparent px-4 py-3 text-sm tracking-[0.04em] text-[#e8d8ca] outline-none transition-colors focus:border-[#c99a74]"
          data-testid="admin-new-password"
        />
      </div>
      {status && (
        <p className="mt-3 text-xs tracking-[0.04em] text-[#c99a74]" data-testid="admin-password-status" aria-live="polite">
          {status}
        </p>
      )}
      {error && (
        <p className="mt-3 text-xs tracking-[0.04em] text-red-400" data-testid="admin-password-error" role="alert">
          {error}
        </p>
      )}
      <button
        type="submit"
        disabled={busy}
        className="mt-5 rounded-full bg-[#5b3829] px-8 py-3 text-[0.62rem] tracking-[0.25em] uppercase text-[#ead8ca] transition-colors duration-300 hover:bg-[#c99a74] hover:text-[#160c08] disabled:opacity-60"
        data-testid="admin-change-password-submit"
      >
        {busy ? "Updating…" : "Update Password"}
      </button>
    </form>
  );
};

const Dashboard = ({ onLogout }) => {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);

  const load = () => {
    axios
      .get(`${API}/admin/signups`, { withCredentials: true })
      .then(({ data }) => setRows(data.signups))
      .catch((err) => {
        if (err.response?.status === 401) onLogout();
        else setError(formatApiErrorDetail(err.response?.data?.detail));
      });
  };

  useEffect(load, []);

  const exportCsv = async () => {
    setExporting(true);
    setError("");
    try {
      const res = await axios.get(`${API}/admin/signups/export`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "lace-and-bond-launch-list.csv";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setExporting(false);
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    onLogout();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: EASE }}
      className="mx-auto w-full max-w-[980px] px-6 py-16"
      data-testid="admin-dashboard"
    >
      <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="font-display text-3xl tracking-[0.06em] text-[#a97855]">
            LACE <span className="font-serif-lux italic text-[#c99a74]">&amp;</span> BOND
          </p>
          <p className="mt-2 text-[0.6rem] tracking-[0.35em] uppercase text-[#bda89a]">
            Private Launch List
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={exportCsv}
            disabled={exporting}
            className="rounded-full border border-[#c99a74]/60 px-6 py-2.5 text-[0.62rem] tracking-[0.25em] uppercase text-[#c99a74] transition-colors duration-300 hover:bg-[#c99a74] hover:text-[#160c08] disabled:opacity-60"
            data-testid="admin-export-csv"
          >
            {exporting ? "Exporting…" : "Export CSV"}
          </button>
          <button
            onClick={logout}
            className="rounded-full border border-[#bda89a]/30 px-6 py-2.5 text-[0.62rem] tracking-[0.25em] uppercase text-[#bda89a] transition-colors duration-300 hover:border-[#c99a74] hover:text-[#c99a74]"
            data-testid="admin-logout"
          >
            Log Out
          </button>
        </div>
      </div>

      <div className="mt-10 flex items-baseline gap-4">
        <span className="font-display text-6xl text-[#c99a74]" data-testid="admin-signup-count">
          {rows ? rows.length : "…"}
        </span>
        <span className="text-[0.62rem] tracking-[0.3em] uppercase text-[#bda89a]">
          {rows && rows.length === 1 ? "Person on the list" : "People on the list"}
        </span>
      </div>

      {error && (
        <p className="mt-4 text-xs text-red-400" data-testid="admin-dashboard-error" role="alert">
          {error}
        </p>
      )}

      <div className="mt-8 overflow-x-auto rounded-2xl border border-[#c99a74]/20">
        <table className="w-full min-w-[560px] text-left text-sm" data-testid="admin-signups-table">
          <thead>
            <tr className="border-b border-[#c99a74]/20 text-[0.58rem] tracking-[0.28em] uppercase text-[#bda89a]">
              <th className="px-5 py-4 font-normal">#</th>
              <th className="px-5 py-4 font-normal">Email</th>
              <th className="px-5 py-4 font-normal">Joined</th>
              <th className="px-5 py-4 font-normal">Welcome Note</th>
            </tr>
          </thead>
          <tbody>
            {rows === null && (
              <tr>
                <td colSpan={4} className="px-5 py-10 text-center text-[#806e63]">
                  Loading the list…
                </td>
              </tr>
            )}
            {rows !== null && rows.length === 0 && (
              <tr>
                <td colSpan={4} className="px-5 py-10 text-center text-[#806e63]">
                  No one has joined yet. Share the site to start building the list.
                </td>
              </tr>
            )}
            {rows &&
              rows.map((row, i) => (
                <tr key={row.email} className="border-b border-[#c99a74]/10 last:border-0 hover:bg-[#c99a74]/5">
                  <td className="px-5 py-4 font-display text-lg text-[#c99a74]">{String(i + 1).padStart(2, "0")}</td>
                  <td className="px-5 py-4 tracking-[0.03em] text-[#e8d8ca]" data-testid="admin-signup-email">
                    {row.email}
                  </td>
                  <td className="px-5 py-4 text-[#bda89a]">
                    {new Date(row.created_at).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </td>
                  <td className="px-5 py-4">
                    {row.unsubscribed ? (
                      <span className="text-[#806e63]" data-testid="admin-welcome-opted-out">Opted out</span>
                    ) : row.welcome_sent ? (
                      <span className="text-[#c99a74]" data-testid="admin-welcome-sent">Sent</span>
                    ) : (
                      <span className="text-[#806e63]">—</span>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <ChangePassword />
    </motion.div>
  );
};

const Admin = () => {
  const [auth, setAuth] = useState("checking");

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then(() => setAuth("in"))
      .catch(() => setAuth("out"));
  }, []);

  return (
    <div className="min-h-screen" data-testid="admin-page">
      <div className="bg-layer fixed -inset-[10%] -z-10" aria-hidden="true" />
      {auth === "checking" && (
        <div className="flex min-h-screen items-center justify-center">
          <p className="text-[0.62rem] tracking-[0.35em] uppercase text-[#806e63]">Loading…</p>
        </div>
      )}
      {auth === "out" && <AdminLogin onLogin={() => setAuth("in")} />}
      {auth === "in" && <Dashboard onLogout={() => setAuth("out")} />}
    </div>
  );
};

export default Admin;
